import { MetricsOptions } from '../../../types';
import {
  NeedsBasedRecOption,
  needsBasedRecAtom,
} from '../../needs-based-rec-selection.atom';
import {
  Container,
  TabText,
  Tabs,
  TabsList,
  TabsTrigger,
} from './content-tabs.style';
import ArticlesTab from './tabs/articles';
import FindARepTab from './tabs/find-a-rep';
import QuizTab from './tabs/quiz';
import { TabProps } from './tabs/types';
import { useTrackLDMetric } from '@exploration/feature-management';
import { sanitize } from 'isomorphic-dompurify';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

// --------------------------------------------------------------------------

const TabMap = {
  articles: ArticlesTab,
  findARep: FindARepTab,
  quiz: QuizTab,
};

// --------------------------------------------------------------------------

export interface ContentTabDataProps {
  [NeedsBasedRecOption.PERSONALIZED_FINANCIAL_PLANNING]: TabProps[];
  [NeedsBasedRecOption.BUDGETING_AND_SAVING]: TabProps[];
  [NeedsBasedRecOption.DAY_TO_DAY_SPENDING]: TabProps[];
  [NeedsBasedRecOption.RETIREMENT_PLANNING]: TabProps[];
  [NeedsBasedRecOption.INVESTING]: TabProps[];
  [NeedsBasedRecOption.GENEROSITY_AND_GIVING]: TabProps[];
  [NeedsBasedRecOption.GETTING_INSURED]: TabProps[];
}

export interface ContentTabsSectionProps {
  contentTabData: ContentTabDataProps;
  className?: string;
  isEmbedded?: boolean;
}

// --------------------------------------------------------------------------

export const ContentTabsSection = ({
  contentTabData,
  className,
  isEmbedded = false,
}: ContentTabsSectionProps) => {
  const [lastSelectedOption, setLastSelectedOption] = useState('');
  const [tabValue, setTabValue] = useState('');
  const [needsBasedData] = useAtom(needsBasedRecAtom);
  const { trackMetric } = useTrackLDMetric();

  // Update tab value, so we always have the first tab open when we switch pills
  useEffect(() => {
    const selectedOption = needsBasedData?.selectedOption;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (selectedOption !== lastSelectedOption) {
      setTabValue(contentTabData?.[selectedOption]?.[0]?.id);
      setLastSelectedOption(selectedOption);
    }
  }, [needsBasedData, contentTabData, lastSelectedOption]);

  // Bail if we don't have an option, or no content
  if (!needsBasedData?.selectedOption || !contentTabData) {
    return null;
  }

  const tabContents = contentTabData[needsBasedData.selectedOption];

  // Bail with a bad option
  if (!Array.isArray(tabContents) || !tabContents.length) {
    return null;
  }

  return (
    <Container className={className} data-testid="ContentTabsSection">
      <Tabs
        defaultValue={tabContents[0].title}
        onValueChange={(value: string) => setTabValue(value)}
        value={tabValue}
      >
        <TabsList>
          {tabContents.map((tabContent) => (
            <TabsTrigger
              key={tabContent.id}
              value={tabContent.id}
              className={tabContent.className}
              aria-label={tabContent.title}
              id={tabContent.id}
            >
              <TabText>{ReactHtmlParser(sanitize(tabContent.title))}</TabText>
            </TabsTrigger>
          ))}
        </TabsList>
        {tabContents.map((tabContent) => {
          const Tab = TabMap[tabContent.type];
          return (
            <Tab
              articles={[]}
              text={''}
              link={{ href: '', text: '' }}
              {...tabContent}
              onClick={() => trackMetric(MetricsOptions.nbrClickOnLinks)}
              key={tabContent.id}
            />
          );
        })}
      </Tabs>
    </Container>
  );
};

// --------------------------------------------------------------------------

export default ContentTabsSection;
